import { useEffect, useRef } from 'react';

/** Consecutive failed connection attempts before the guest stream gives up. */
export const GUEST_PRICE_EVENTS_MAX_ERRORS = 6;
const BASE_RETRY_MS = 2_000;
const MAX_RETRY_MS = 60_000;

/**
 * Subscribes to the public stream that says a restaurant's guest prices
 * changed. Unlike the admin stream this fails silently: the stream is a nudge,
 * checkout still validates the price, so a guest never needs to see an error.
 * The browser reconnects on its own after a dropped stream; if it gives up
 * (HTTP error), this retries with backoff, up to a cap.
 */
export const useGuestPriceEvents = (
  restaurantId: string | undefined,
  onPricesChanged: () => void,
) => {
  const onPricesChangedRef = useRef(onPricesChanged);
  onPricesChangedRef.current = onPricesChanged;

  useEffect(() => {
    if (!restaurantId || typeof EventSource === 'undefined') return;

    let eventSource: EventSource | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let consecutiveErrors = 0;

    const connect = () => {
      const source = new EventSource(
        `${process.env.API_HOST}/guest/restaurants/${restaurantId}/prices/sse`,
      );
      eventSource = source;

      source.addEventListener('open', () => {
        consecutiveErrors = 0;
      });
      source.addEventListener('prices_changed', () => {
        onPricesChangedRef.current();
      });
      source.onerror = () => {
        consecutiveErrors += 1;
        if (consecutiveErrors >= GUEST_PRICE_EVENTS_MAX_ERRORS) {
          source.close();
          return;
        }
        // While CONNECTING the browser is already reconnecting by itself.
        if (source.readyState !== EventSource.CLOSED) return;
        retryTimer = setTimeout(
          connect,
          Math.min(BASE_RETRY_MS * consecutiveErrors, MAX_RETRY_MS),
        );
      };
    };

    connect();

    return () => {
      clearTimeout(retryTimer);
      eventSource?.close();
    };
  }, [restaurantId]);
};
